import { useState } from "react";
import type { EarningsParams, EarningsRange } from "../api";

/**
 * Period picker for the Earnings widgets: preset chips (8) plus a
 * custom row with two native `<input type="date">` boxes and an apply
 * button. `value` is the full `EarningsParams`; `onChange` fires once
 * per settled change (chip tap, or Apply in custom mode), so the
 * debounce lives here. Chips rather than a `<select>`: one tap = one
 * fetch, and a native select hijacks the scrolling viewport on iOS.
 * docs/customer-app.md > Front-end > Period picker
 */

const PRESETS: { key: EarningsRange; label: string }[] = [
  { key: "today",      label: "Vandaag" },
  { key: "yesterday",  label: "Gisteren" },
  { key: "last_7d",    label: "7 d" },
  { key: "last_30d",   label: "30 d" },
  { key: "month",      label: "Maand" },
  { key: "prev_month", label: "Vorige maand" },
  { key: "year",       label: "Jaar" },
  { key: "prev_year",  label: "Vorig jaar" },
];

interface Props {
  value: EarningsParams;
  onChange: (next: EarningsParams) => void;
}

export function PeriodPicker({ value, onChange }: Props) {
  const today = isoDateLocal(new Date());
  // `showCustom` controls whether the date-input row is visible.
  // It's INDEPENDENT of `value.range === "custom"` because tapping
  // Aangepast must NOT immediately fire onChange - that would push
  // an invalid {range: "custom"} (no start/end) up to the parent and
  // trigger the backend's 422. Only Toepassen commits a range=custom
  // params object. Initialised from the prop so a refresh on a
  // previously-applied custom range keeps the inputs visible.
  const [showCustom, setShowCustom] = useState<boolean>(value.range === "custom");

  // Sensible defaults: a 7-day window ending today. A {today→today}
  // range is zero-width - Toepassen would just bounce as invalid.
  const sevenDaysAgo = isoDateLocal(new Date(Date.now() - 7 * 86_400_000));
  const [customStart, setCustomStart] = useState<string>(() =>
    value.range === "custom" && value.start
      ? isoDateLocal(new Date(value.start * 1000))
      : sevenDaysAgo,
  );
  const [customEnd, setCustomEnd] = useState<string>(() =>
    value.range === "custom" && value.end
      ? isoDateLocal(new Date(value.end * 1000))
      : today,
  );

  function pickPreset(k: EarningsRange) {
    setShowCustom(false);
    onChange({ range: k });
  }
  function toggleCustom() {
    // Show / hide inputs WITHOUT firing onChange. The parent's range
    // stays on the last preset until the user picks Toepassen.
    setShowCustom((v) => !v);
  }
  function applyCustom() {
    // Inputs are dates in local time; convert to start-of-day and
    // end-of-NEXT-day for an inclusive range (the user picking May 1
    // through May 3 wants May 1 00:00 → May 4 00:00).
    const start = Math.floor(new Date(`${customStart}T00:00:00`).getTime() / 1000);
    const end   = Math.floor(new Date(`${customEnd}T00:00:00`).getTime() / 1000) + 86400;
    if (end <= start) return;
    onChange({ range: "custom", start, end });
  }

  // "Aangepast" chip highlights when the inputs are open OR when the
  // parent is currently showing a custom-applied range.
  const customActive = showCustom || value.range === "custom";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <Chip
            key={p.key}
            label={p.label}
            active={value.range === p.key && !showCustom}
            onClick={() => pickPreset(p.key)}
          />
        ))}
        <Chip
          label="Aangepast"
          active={customActive}
          onClick={toggleCustom}
        />
      </div>
      {showCustom && (
        <div className="flex flex-wrap items-end gap-2 mt-1">
          <DateField label="Van"  value={customStart} onChange={setCustomStart} max={today} />
          <DateField label="Tot"  value={customEnd}   onChange={setCustomEnd}   max={today} />
          <button
            type="button"
            onClick={applyCustom}
            className="bg-accent text-white text-xs font-medium rounded-full px-3 py-1.5"
          >
            Toepassen
          </button>
        </div>
      )}
    </div>
  );
}

function Chip({
  label, active, onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
        active
          ? "bg-accent text-white font-medium"
          : "bg-card-soft text-ink-mute hover:text-ink"
      }`}
    >
      {label}
    </button>
  );
}

function DateField({
  label, value, onChange, max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-ink-mute">{label}</span>
      <input
        type="date"
        value={value}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="bg-card-soft rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
      />
    </label>
  );
}

/** "yyyy-mm-dd" for a Date interpreted in LOCAL time - what
 *  `<input type="date">` expects. Date.prototype.toISOString() returns
 *  UTC which would shift the visible date for users east of UTC. */
function isoDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
